<template>
  <div class="boolean-filter" :class="classModifiers">
    <div v-if="$slots.field" class="boolean-filter__field">
      <slot
        name="field"
        :checked="checked"
        :disabled="checkboxDisabled"
        :label="optionText"
        :test-id="fieldOptionTestId"
        :handle-update-checked="handleUpdateChecked"
      />
    </div>
    <sp-menu
      class="boolean-filter__menu"
      :open="open"
      :disabled="disabled"
      :location="location"
      :pointer="showPointer"
      :close-on-content-click="false"
      @update-open="handleUpdateOpen"
    >
      <FilterActivatorSlot
        :label="label"
        :text="selectedText"
        :prepend-icon="prependIcon"
        :append-icon="appendIcon"
        :open="open"
        :non-clearable="isNonClearable"
        :disabled="disabled"
        @clear="clear"
      >
        <template v-if="$slots.activator" #activator="activatorProps">
          <slot name="activator" v-bind="activatorProps" />
        </template>
      </FilterActivatorSlot>

      <sp-card variant="new-default">
        <sp-card-content>
          <sp-text tag="h3" type="custom" class="boolean-filter__heading">{{ label }}</sp-text>

          <div class="boolean-filter__option">
            <sp-checkbox
              v-preserve-properties:checked="checked"
              class="boolean-filter__checkbox"
              :label="optionText"
              :disabled="checkboxDisabled"
              :data-testid="menuOptionTestId"
              @update-checked="handleUpdateChecked"
            ></sp-checkbox>

            <sp-tooltip v-if="helpContent" class="boolean-filter__help" :content="helpContent" position="top">
              <sp-icon name="help-filled" size="small"></sp-icon>
            </sp-tooltip>
          </div>
        </sp-card-content>
      </sp-card>
    </sp-menu>
  </div>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { computed, ref, useSlots, watch } from "vue";
import { toBoolean, trimToUndefined } from "#ce/utils/props";
import { firstDetail } from "#ce/utils/events";
import { useForm } from "#ce/composables/useForm";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useControllableOpen } from "#ce/shared/filters/composables/useControllableOpen";
import { props as booleanFilterProps } from "#ce/shared/filters/boolean/components/props";
import FilterActivatorSlot from "#ce/shared/filters/components/FilterActivatorSlot.ce.vue";

const emit = defineEmits(["update:model-value", "update:open"]);

const props = defineProps(booleanFilterProps);
const slots = useSlots();
const { isGtSmScreen } = useBreakpoints();
const showsInlineField = computed(() => Boolean(slots.field) && isGtSmScreen.value);
const fieldOptionTestId = computed(() => (showsInlineField.value ? props.optionTestId : undefined));
const menuOptionTestId = computed(() => (showsInlineField.value ? undefined : props.optionTestId));

/** The value a ticked box reports and submits: the `<name>=true` the finder's URL carries. */
const CHECKED_VALUE = "true";

const checked = ref(toBoolean(props.modelValue));

// A prop sync mirrors silently; only the visitor's toggle reports a change upward.
function syncCheckedFromModelValue(value) {
  checked.value = toBoolean(value);
}

watch(() => props.modelValue, syncCheckedFromModelValue);

const isNonClearable = computed(() => toBoolean(props.nonClearable));
const canClear = computed(() => !isNonClearable.value);
const showPointer = computed(() => !toBoolean(props.hidePointer));

// Unticking is clearing, so a non-clearable filter's ticked box takes no click.
const lockedOn = computed(() => checked.value && isNonClearable.value);
const checkboxDisabled = computed(() => props.disabled || lockedOn.value);

const optionText = computed(() => trimToUndefined(props.optionLabel) ?? props.label);
const helpContent = computed(() => trimToUndefined(props.helpText));

/** The activator text while ticked: `selectedLabel`, falling back to the option's wording. */
const selectedText = computed(() =>
  checked.value ? (trimToUndefined(props.selectedLabel) ?? optionText.value) : undefined,
);

// An unticked box reports `undefined`, which the host reads as inactive.
function emitModelValue() {
  emit("update:model-value", checked.value ? CHECKED_VALUE : undefined);
}

const { mirrorFormValue } = useForm();

mirrorFormValue(computed(() => (checked.value ? CHECKED_VALUE : null)));

const open = useControllableOpen(() => props.open, emit);

// `update-checked` is the checkbox's user-input path alone, so this is the one place a change is
// reported. A toggle completes the pick, so it closes the menu.
function handleUpdateChecked(event) {
  if (toBoolean(props.disabled)) {
    return;
  }

  const next = Boolean(firstDetail(event));

  if (next === checked.value) {
    return;
  }

  checked.value = next;
  emitModelValue();
  open.value = false;
}

function handleUpdateOpen(event) {
  if (toBoolean(props.disabled)) {
    return;
  }

  open.value = firstDetail(event);
}

function clear() {
  if (toBoolean(props.disabled)) {
    return;
  }

  if (!checked.value || !canClear.value) {
    return;
  }

  checked.value = false;
  emitModelValue();
}

const classModifiers = computed(() => ({
  "--has-selected-model": checked.value,
  "--field": Boolean(slots.field),
}));

defineExpose({
  props,
  active: checked,
  selectedText,
});
</script>

<style>
:host {
  --filter-width: 100%;
  --sp-comp-popup-width: max-content;
  display: block;
  box-sizing: border-box;
}
:host([chrome="bar"]) {
  --filter-width: max-content;
  --sp-comp-popup-width: var(--sp-comp-filter-bar-popup-width, 18.5rem);
}
</style>

<style lang="scss" scoped>
@import "@assets/mixins/breakpoint";

.boolean-filter {
  --sp-comp-button-width: var(--filter-width);

  display: flex;
  align-items: center;
  height: 100%;
  width: var(--filter-width);

  &__field {
    display: none;
    width: 100%;
  }

  &.--field {
    @include sp-breakpoint(sm) {
      --sp-comp-finder-filter-field-height: var(--sp-comp-finder-filter-field-boolean-height, var(--sp-ref-scale-40));

      .boolean-filter__field {
        display: block;
      }

      .boolean-filter__menu {
        display: none;
      }
    }
  }

  &__menu {
    width: var(--filter-width);
  }

  &__heading {
    --sp-comp-text-custom-font-short: var(
      --sp-comp-boolean-filter-heading-font,
      var(--sp-sys-typescale-body-bold-short)
    );

    display: block;
    margin-bottom: var(--sp-ref-scale-12);
  }

  &__option {
    display: flex;
    align-items: center;
    gap: var(--sp-ref-scale-8);
  }

  &__checkbox {
    --option-font: var(--sp-comp-boolean-filter-option-font, var(--sp-sys-typescale-body-short));
    --sp-comp-checkbox-label-font: var(--option-font);
    --sp-comp-text-label-short: var(--option-font);
  }

  &__help {
    display: inline-flex;
    --fill: var(--sp-sys-color-primary);
  }

  .activator {
    width: var(--filter-width);
  }

  &.--has-selected-model .activator {
    --sp-comp-text-field-color: var(--sp-sys-color-on-surface);
  }
}
</style>
